import type { ReactNode } from 'react';
import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@lqa-frontend/ui-system';

export enum TabSolidIconPosition {
	Left = 'left',
	Right = 'right',
}

export interface ITabSolidOption<T = string | number> {
	label: string;
	value: T;
	icon?: ReactNode;
	isDisabled?: boolean;
}

export interface ITabSolidProps<T = string | number> {
	options: ITabSolidOption<T>[];
	value: T;
	onValueChange: (value: T) => void;
	className?: string;
	listClassName?: string;
	triggerClassName?: string;
	/** Which side of the label each option's icon sits on. */
	iconPosition?: TabSolidIconPosition;
}

function TabSolid<T extends string | number>({
	options,
	value,
	onValueChange,
	listClassName,
	triggerClassName,
	className,
	iconPosition = TabSolidIconPosition.Left,
}: ITabSolidProps<T>) {
	const stringValue = String(value);

	const handleValueChange = (newValue: string) => {
		const option = options.find((opt) => String(opt.value) === newValue);
		if (option) onValueChange(option.value);
	};

	return (
		<TabsPrimitive.Root
			value={stringValue}
			onValueChange={handleValueChange}
			className={cn('flex flex-col', className)}
			data-testid="tab-solid"
		>
			<TabsPrimitive.List
				className={cn(
					'inline-flex w-fit items-center bg-base-2 rounded-m p-1 shadow-xs',
					listClassName
				)}
			>
				{options?.map((option) => (
					<TabsPrimitive.Trigger
						key={String(option.value)}
						value={String(option.value)}
						disabled={option.isDisabled}
						className={cn(
							'text-12',
							'relative inline-flex items-center justify-center gap-8 cursor-pointer whitespace-nowrap px-8 py-6 rounded-s transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2',
							'text-neutral hover:text-text-primary',
							'data-[state=active]:bg-white font-semibold  data-[state=active]:text-text-primary data-[state=active]:shadow-sm',
							'disabled:pointer-events-none disabled:text-text-tertiary',
							triggerClassName
						)}
						data-testid={`tab-solid-trigger-${option.value}`}
					>
						{iconPosition === TabSolidIconPosition.Left && option.icon}
						{option.label}
						{iconPosition === TabSolidIconPosition.Right && option.icon}
					</TabsPrimitive.Trigger>
				))}
			</TabsPrimitive.List>
		</TabsPrimitive.Root>
	);
}

export { TabSolid };
